import { withDefaults } from '../../../core/theme';
import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  CARD_ASPECT, add, card, fade, fill,
} from '../fx/common';
import { OBJECT_STYLE } from '../drawObjects';

const objectStyle = withDefaults(() => [OBJECT_STYLE]);

/**
 * **破壊（場から）**（Issue #556。指示書 `../../direction/move.md`）。モンスターゾーン・魔法罠ゾーンのカードが
 * **ひびが走って割れ、砕け散る**だけ。文字（DESTROY! など）も追加のモーションも無い。
 * 戦闘破壊も効果破壊も、行き先がどこでも（破壊されて除外も）同じ絵。
 *
 * ```
 * 0     盤のカードが震え、衝撃点から硬い白いひびが走る（近いひびから順に）
 * 200   割れる: 盤面が進んで盤のカードが消え、ひびの形どおりの破片に分かれる。硬い閃光（2 フレーム）と火花。止め 60ms
 * 260   砕け散る: 破片が回りながら外へ飛び、落ちながら消える。軽い揺れ
 * 900   終わり（破片・火花は 880 までに消える）
 * ```
 *
 * **盤面は割れる瞬間（`BREAK`）にイベントの後の状態へ進める**（口 `board`。Issue #566）。それまでは盤面が
 * 移動前のままなので、**盤のカードそのものを `tweaks` で借りて震わせ**、その上に演出の板でひびを描く。
 * 割れた瞬間に盤面が進んでカードは盤から消え（墓地の枚数が増える）、同じ瞬間から演出の板の破片が砕け散る。
 * 前のイベントが盤面を進めるのが遅れて割れる瞬間にまだ盤に居たら、割れたあとは借りたまま隠す。
 * 破片の絵（表裏・向き）は移動前の盤面（`boardBefore`。`opens` が `prev` に入れる）から、置き場所は元のゾーンから引く。
 * uid が分からない（盤のカードを借りられない）ときは盤面を帯の頭で進め、割れる前のカードも演出の板で描く（以前の形）。
 *
 * まとめて壊れるときは、次の「場からの破壊」が前の帯の頭から 120ms で重なる（`overlap`。ぱぱぱぱりん）。
 */
export const D = 900;
const CRACK_IN = 20;
export const BREAK = 200;
const STOP = 60;
const FLY = BREAK + STOP;
const FADE_FROM = 640;
const GONE = 880;
/** 重ねる間（前の帯の頭から。ユーザー 2026-09-28） */
export const STACK_MS = 120;
export const OVERLAP_KEY = 'move:destroy';

const RAYS = 7;
const RINGS = 2;
const SPARKS = 12;

const liftOf = (prev, style, spell) => {
  const v = objectStyle(style);
  return !spell && prev?.faceUp && !prev?.defense ? v.attackLift : v.cardLift;
};
/** 盤の上の回し（`pos_change.js::turnOf` と同じ規則。伏せた魔法・罠は 180°、守備表示は 90°） */
const turnOf = (prev, spell) => {
  if (spell) return prev?.faceUp ? 0 : Math.PI;
  return prev?.defense ? Math.PI / 2 : 0;
};

/**
 * 割れるまでの震え（カードの幅に対する比の `[x, z]`）。30ms ごとに向きの変わる小刻みの揺れが強くなる。
 * 盤のカード（`tweaks`）と演出の板（ひび）が**同じ値**で揺れる。
 */
export function jitterAt(t) {
  const k = t < BREAK ? seg(t, 0, BREAK) : 0;
  const step = Math.floor(t / 30);
  return [Math.sin(step * 2.3 + 0.7) * 0.035 * k, Math.cos(step * 3.1) * 0.025 * k];
}

/** 盤のカードを借りるか（uid が分かるときだけ。分からなければ演出の板で描き直す） */
const borrows = (payload) => Boolean(payload?.uid && payload?.fromZone);

/** 板を線分 a → b の上に置く（盤の上。幅 wd） */
function lay(m, a, b, k, wd, y) {
  const ex = a[0] + (b[0] - a[0]) * k;
  const ez = a[1] + (b[1] - a[1]) * k;
  const len = Math.hypot(ex - a[0], ez - a[1]);
  m.position.set((a[0] + ex) / 2, y, (a[1] + ez) / 2);
  m.rotation.y = -Math.atan2(b[1] - a[1], b[0] - a[0]);
  m.scale.set(Math.max(0.001, len), 1, Math.max(0.001, wd));
  return len;
}

export default defineEffect({
  situation: 'move_destroy',
  role: 'motion',
  label: '破壊（場から。ひび → 割れる → 砕け散る）',
  color: '#b18cff',
  duration: () => D,
  sounds: () => [{ at: BREAK - 20, name: 'destoroy' }],
  scene: () => ['reset',
    {
      ms: BREAK,
      grade: { contrast: { keys: [{ ms: 0, value: 1.3 }, { ms: STOP, value: 1.3, ease: 'hold' }, { ms: STOP + 1, value: 1 }] } },
    },
    { ms: FLY, shake: { amp: 9, freq: 28, decayMs: 260 } },
  ],
  overlap: () => ({ key: OVERLAP_KEY, after: { [OVERLAP_KEY]: STACK_MS } }),
  // 盤面は割れる瞬間にイベントの後へ（それまで盤のカードは元のゾーンに居る。Issue #566）
  board: ({ payload }) => (borrows(payload) ? { at: BREAK } : null),
  /*
   * **盤のカードを借りる**（R13）。割れるまでは震わせ、割れる瞬間に少し明るくする。
   * 割れたあとも元のゾーンに居れば（盤面が進むのが遅れたとき）隠す。**元のゾーンの板にだけ**当てる
   * （盤面が進んだあとは同じ uid が墓地などに居るので触らない）
   */
  tweaks: ({ payload, localMs: t }) => {
    if (!borrows(payload) || t >= D) return {};
    const id = `card:${payload.fromZone}`;
    return {
      [payload.uid]: (h) => {
        if (h.part?.id && h.part.id !== id) return;
        h.reset?.();
        if (t >= BREAK) { h.root.visible = false; return; }
        const w = h.part?.size?.w ?? 0;
        const [jx, jz] = jitterAt(t);
        h.root.position.x += jx * w;
        h.root.position.z += jz * w;
        if (h.fx) h.fx.brightness.value = 1 + 0.35 * seg(t, BREAK - 70, BREAK);
      },
    };
  },
  layers: [
    {
      id: 'shatter',
      label: '割れて砕け散るカード',
      space: 'board',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const f = ctx.stage?.field;
        const unit = f?.unit > 0 ? f.unit : 0;
        const plane = f?.plane ?? null;
        const at = payload?.fromZone ? ctx.anchor?.({ zone: payload.fromZone }) : null;
        if (!at?.plane || !(unit > 0) || !plane) return { root };
        const prev = payload.prev ?? {};
        const spell = payload.fromKind === 'magic_trap';
        const faceUp = prev.faceUp !== false;
        const thick = { ...OBJECT_STYLE, ...(ctx.style ?? {}) }.cardThickness;
        const y0 = liftOf(prev, ctx.style, spell) + thick;
        const [wx, wz] = [at.plane[0] - plane.w / 2, at.plane[1] - plane.h / 2];
        const yaw = (Number(f.cardAngle?.(at.zone ?? payload.fromZone)) || 0) + turnOf(prev, spell);
        const cw = unit;
        const ch = unit / CARD_ASPECT;
        const rand = seeded(`${ctx.seed}:destroy`);

        // カード 1 枚ぶんの座標（盤の上。x = 絵の右、z = 絵の下）
        const grp = new THREE.Group();
        add(root, grp, 20);
        grp.position.set(wx, y0, wz);
        grp.rotation.y = yaw;

        // 絵（破片のテクスチャ）。盤のカードを借りるときは割れる前も見せない（盤のカードが見える）
        const borrowed = borrows(payload);
        const whole = card(ctx, { code: faceUp ? (prev.code ?? payload.code ?? null) : null, player: payload.player, faceUp, w: cw });
        const tex = whole.mesh.material.map ?? null;
        add(grp, kit.onBoard(whole.mesh), 20.01);

        // 破片（ひびと同じ線で割れる）。絵が届いていなければ地の色
        const pieces = kit.crackShards(tex, cw, ch, `${ctx.seed}:cracks`, {
          rays: RAYS,
          rings: RINGS,
          material: (t) => {
            const m = kit.cardArtMaterial(t, { brightness: 1.15, saturation: 1.3 });
            if (!t) m.color?.set?.(faceUp ? '#28324e' : '#2a1a4d');
            return m;
          },
        });
        const shards = pieces.children.map((m) => {
          kit.onBoard(m);
          const sp = cw * (1.3 + rand() * 1.7) * (0.7 + 0.6 * m.userData.dist);
          return {
            m,
            ...m.userData,
            sp,
            up: cw * (1.2 + rand() * 1.6),
            spinX: (rand() - 0.5) * 16,
            spinZ: (rand() - 0.5) * 16,
          };
        });
        const shardGrp = new THREE.Group();
        shards.forEach((s) => add(shardGrp, s.m, 20.02));
        add(grp, shardGrp, 20.02);

        // ひび（暗い縁の上に白い芯。近い端から伸びる）。衝撃点に近い端を a にする
        const { impact } = pieces.userData;
        const near = (p) => Math.hypot(p[0] - impact[0], p[1] - impact[1]);
        const cracks = pieces.userData.cracks.map((c) => {
          const [a, b] = near(c.a) <= near(c.b) ? [c.a, c.b] : [c.b, c.a];
          const dark = kit.onBoard(fill(ctx, '#07070a', 1, 1));
          const white = kit.onBoard(fill(ctx, '#ffffff', 1, 1));
          add(grp, dark, 20.03);
          add(grp, white, 20.031);
          // 板の座標は絵の座標（x, y 下向き）→ 盤の (x, z)
          return { a, b, d: c.d, dark, white, at: CRACK_IN + c.d * (BREAK - CRACK_IN - 50) };
        });

        // 割れた瞬間の硬い閃光（カードの形の白。2 フレーム）
        const flash = kit.onBoard(fill(ctx, '#ffffff', cw * 1.06, ch * 1.06));
        add(grp, flash, 20.04);

        // 硬い火花（衝撃点から外へ伸びる細い線）
        const sparks = Array.from({ length: SPARKS }, (_, i) => {
          const a = (i / SPARKS) * Math.PI * 2 + (rand() - 0.5) * 0.5;
          const m = kit.onBoard(fill(ctx, i % 3 === 0 ? '#ffd21a' : '#ffffff', 1, 1));
          add(grp, m, 20.05);
          return { m, dir: [Math.cos(a), Math.sin(a)], reach: cw * (0.9 + rand() * 0.9), len: cw * (0.18 + rand() * 0.2) };
        });

        return {
          root,
          update: ({ localMs: t }) => {
            // 震え（盤のカードの `tweaks` と同じ値。ひびが盤のカードに付いて揺れる）
            const [jx, jz] = jitterAt(t);
            grp.position.set(wx + jx * cw, y0, wz + jz * cw);

            fade(whole.mesh, !borrowed && t < BREAK ? 1 : 0);

            // ひび: 近い順に走る → 割れて止まっている間まで残る
            const yc = 0.4;
            cracks.forEach((c) => {
              const k = tween(t, c.at, c.at + 50, 'ease_out');
              const on = t >= c.at && t < FLY;
              lay(c.dark, c.a, c.b, k, cw * 0.034, yc);
              lay(c.white, c.a, c.b, k, cw * 0.013, yc + 0.1);
              fade(c.dark, on && k > 0 ? 0.85 : 0);
              fade(c.white, on && k > 0 ? 1 : 0);
            });

            // 破片: 割れて止まる間は少しだけ開き、止めが明けたら飛ぶ
            const u = Math.max(0, (t - FLY) / 1000);
            const out = 1 - seg(t, FADE_FROM, GONE);
            shards.forEach((s) => {
              const open = t >= BREAK ? cw * 0.03 * (0.5 + s.dist) : 0;
              const r = open + s.sp * u;
              const yy = s.up * u - cw * 9 * u * u;
              s.m.position.set(s.x0 + s.dir[0] * r, 0.2 + Math.max(-y0 + 0.5, yy), s.y0 + s.dir[1] * r);
              s.m.rotation.set(s.spinX * u, 0, s.spinZ * u);
              const sc = 1 - 0.35 * seg(t, FLY, GONE);
              s.m.scale.set(sc, sc, sc);
              fade(s.m, t >= BREAK && t < GONE ? out : 0);
            });

            fade(flash, t >= BREAK && t < BREAK + 34 ? 0.95 : 0);

            // 火花: 割れた瞬間に外へ飛んで細くなる
            const sk = tween(t, BREAK, BREAK + 220, 'expo_out');
            const thin = 1 - seg(t, BREAK + 60, BREAK + 240);
            sparks.forEach((s) => {
              const head = [impact[0] + s.dir[0] * s.reach * sk, impact[1] + s.dir[1] * s.reach * sk];
              const tail = [head[0] - s.dir[0] * s.len * thin, head[1] - s.dir[1] * s.len * thin];
              lay(s.m, tail, head, 1, cw * 0.022 * thin, 0.6);
              fade(s.m, t >= BREAK && thin > 0.02 ? 1 : 0);
            });
          },
        };
      },
    },
  ],
}, 'move_destroy_motion.js');
